import { Component, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { ZzzConfigService } from 'src/app/zzz/core/config/config.service';
import { AbstractControl, FormGroup, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { Feature, GeometryFormFieldInputComponent, IgoMap } from '@igo2/geo';
import { MapState } from '@igo2/integration';
import type { Type } from 'ol/geom/Geometry';
import OlFeature from 'ol/Feature';
import type { default as OlGeometry } from 'ol/geom/Geometry';
import * as olStyle from 'ol/style';
import { HttpClient } from '@angular/common/http';
import { DomainesService } from 'src/app/shared/services/domaines.service';
import { ValeurDomaineDto } from 'src/app/shared/models/domaines.dto';
import { CommentaireGeneralService } from 'src/app/shared/services/commentaire-general.service';
import { SigtUtilisateurService } from 'src/app/shared/services/sigt-utilisateur.service';
import { Constantes } from 'src/app/shared/constantes';
import { ToolComponent } from '@igo2/common/tool';
import { MessageService } from '@igo2/core/message';
import { LanguageService } from '@igo2/core/language';
import { MatFormFieldModule } from '@angular/material/form-field';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';

@ToolComponent({
  name: 'sigt-commenter-general',
  title: 'igo.integration.tools.sigtCommenterGeneral',
  icon: 'comment-text-outline'
})
@Component({
    selector: 'app-sigt-commenter-general',
    templateUrl: './sigt-commenter-general.component.html',
    styleUrls: ['./sigt-commenter-general.component.scss'],
    imports: [
        CommonModule,
        ReactiveFormsModule,
        TranslateModule,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatIconModule,
        GeometryFormFieldInputComponent
    ]
})
export class SigtCommenterGeneralComponent implements OnInit {
  form: UntypedFormGroup;
  messageApplication: { [key: string]: string } = {};
  nom: BehaviorSubject<string> = new BehaviorSubject("");
  courriel: BehaviorSubject<string> = new BehaviorSubject("");
  nomEmployeur: BehaviorSubject<string> = new BehaviorSubject("");
  listeTypeAvis: ValeurDomaineDto[];
  @ViewChild('commentaire') commentaireElement: ElementRef;
  valeurTypeAvisDefaut: string;

  get map(): IgoMap {
    return this.mapState.map;
  }

  geometryType: Type = 'Point';
  radius: number = 1000;
  drawStyle$: BehaviorSubject<olStyle.Style | ((feature, resolution) => olStyle.Style)> = new BehaviorSubject(undefined);

  // TerrAPI du MSI, par la route /terrapi de la passerelle SIGT (l'adresse
  // geoegl.msp.gouv.qc.ca/apis/terrapi cesse de fonctionner le 2027-01-27).
  readonly #urlTerrApi = inject(ZzzConfigService).get().authApiEndPoint + '/terrapi';

  constructor(
    private formBuilder: UntypedFormBuilder,
    private utilisateurService: SigtUtilisateurService,
    private mapState: MapState,
    private messageService: MessageService,
    private languageService: LanguageService,
    private httpClient: HttpClient,
    private commentaireGeneralService: CommentaireGeneralService,
    private domainesService: DomainesService
  ) { }

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      positionSurCarte: ["", [Validators.required]],
      positionSurCarteForViewOnly: ["", [Validators.required]],
      codeRegionAdministrative: ["", [Validators.required]],
      utilisateurId: [this.utilisateurService.current.id, [Validators.required]],
      commentaire: ["", [Validators.required, Validators.minLength(10), this.validerPattern]],
      typeAvis: ["", [Validators.required]],
      noDossier: ["", [Validators.maxLength(20)]],
      objet: ["", [Validators.required, Validators.maxLength(200)]]
    }, { validators: [this.validerChamps.bind(this)] });

    // Chercher les détails de l'utilisateur
    this.nom.next(this.utilisateurService.current.nom + ' ' + this.utilisateurService.current.prenom);
    this.courriel.next(this.utilisateurService.current.courriel);
    this.nomEmployeur.next(this.utilisateurService.current.nomMinistereOrganisme);

    this.messageApplication.echecOperation = Constantes.MessageApplicatif.OperationNonComplete_11E;
    this.messageApplication.succesOperation = Constantes.MessageApplicatif.ElementEnregistrer_7I;
    this.messageApplication.invalideCommentaireCourt = Constantes.MessageApplicatif.CommentaireAuMoins10_20E;
    this.messageApplication.invalideCommentairePattern = Constantes.MessageApplicatif.Invalide_2E.replace("{0}",
      this.languageService.translate.instant('sigt.integration.commenterGeneral.form.messagesPlaceholders.invalideCommentairePattern'));
    this.messageApplication.invalidePointSurCarte = Constantes.MessageApplicatif.Information_22E.replace("{0}",
      this.languageService.translate.instant('sigt.integration.commenterGeneral.form.messagesPlaceholders.invalidePointSurCarte'));
    this.messageApplication.obligatoireObjet = Constantes.MessageApplicatif.ValeurObligatoire_1E.replace("{0}",
      this.languageService.translate.instant('sigt.integration.commenterGeneral.form.commentaire.erreurObjet'));
    this.messageApplication.obligatoireCommentaire = Constantes.MessageApplicatif.ValeurObligatoire_1E.replace("{0}",
      this.languageService.translate.instant('sigt.integration.commenterGeneral.form.commentaire.erreurCommentaire'));

    this.form.controls.positionSurCarte.valueChanges.subscribe(positionSurCarte => {
      if (positionSurCarte) {
        this.httpClient.get<{ features: Feature[] }>(`${this.#urlTerrApi}/locate?type=regadmin&loc=${positionSurCarte.coordinates.join(",")}`).subscribe(features => {
          if (features.features.length > 0) {
            this.form.patchValue({
              codeRegionAdministrative: features.features[0].properties.code,
              positionSurCarteForViewOnly: `${positionSurCarte.coordinates[0]}, ${positionSurCarte.coordinates[1]}`
            });
          } else {
            this.messageService.error(this.messageApplication.invalidePointSurCarte);
            this.form.patchValue({
              positionSurCarte: '',
              codeRegionAdministrative: '',
              positionSurCarteForViewOnly: ''
            });
          }
          this.commentaireElement.nativeElement.focus(); //cette ligne est nécessaire. Sans ce focus, bien que le formulaire soit valide, le bouton soumettre ne va pas etre activer
        });
      }
    });

    this.initialiserGeometryFieldInput();

    this.domainesService.obtenirValeurs("TYPE_AVIS")
      .subscribe({
        next: (data) => {
          this.listeTypeAvis = data.valeursDomaine;
          if (this.listeTypeAvis.length > 0) {
            this.valeurTypeAvisDefaut = this.listeTypeAvis[0].valeur;
            this.form.patchValue({ typeAvis: this.valeurTypeAvisDefaut });
          }
        },
        error: (error) => {
          console.log("Erreur: " + JSON.stringify(error));
        },
      }
      );
  }

  submitForm() {
    if (this.form.valid) {
      const formValue = this.form.value;
      this.commentaireGeneralService.creerCommentaire({
        utilisateurId: formValue.utilisateurId,
        commentaire: formValue.commentaire,
        geometrieX: formValue.positionSurCarte.coordinates[0],
        geometrieY: formValue.positionSurCarte.coordinates[1],
        noRegionAdmin: formValue.codeRegionAdministrative,
        typeAvis: formValue.typeAvis,
        noDossier: formValue.noDossier,
        objet: formValue.objet
      }).subscribe({
        next: rep => {
          this.messageService.info(this.messageApplication.succesOperation);
          this.initialiserChamps();
        },
        error: _err => {
          this.messageService.error(this.messageApplication.echecOperation);
        }
      });
    }
  }

  validerPattern(ctrl: AbstractControl): ValidationErrors | null {
    if (ctrl.value?.length >= 10 //pour éviter de montrer une erreur avant de saisir le minimum des caracteres
      && ctrl.value.match(Constantes.regexValidationCommentaire) === null) {
      return {
        pattern: true
      };
    }
    return null;
  }

  initialiserGeometryFieldInput() {
    const pointStyle = (
      _feature: OlFeature<OlGeometry>,
      _resolution: number
    ) => {
      return new olStyle.Style({
        image: new olStyle.Circle({
          radius: 5,
          stroke: new olStyle.Stroke({
            width: 2,
            color: 'rgba(0, 153, 255)'
          }),
          fill: new olStyle.Fill({
            color: 'rgba(0, 153, 255, 0.2)'
          })
        })
      });
    };

    this.drawStyle$.next(pointStyle);
  }

  initialiserChamps() {
    this.form.patchValue({
      positionSurCarte: '',
      positionSurCarteForViewOnly: '',
      codeRegionAdministrative: '',
      commentaire: '',
      typeAvis: this.valeurTypeAvisDefaut,
      noDossier: '',
      objet: ''
    });
    this.form.get('positionSurCarteForViewOnly').setErrors(null, { emitEvent: false });
    this.form.get('commentaire').setErrors(null, { emitEvent: false });
    this.form.get('objet').setErrors(null, { emitEvent: false });
  }

  validerChamps(control: FormGroup): any {
    if (control.value.objet || control.value.commentaire || control.value.typeAvis || control.value.positionSurCarteForViewOnly) {
      const objet = control.controls["objet"].value;
      const commentaire = control.controls["commentaire"].value;
      const typeAvis = control.controls["typeAvis"].value;
      const positionSurCarteForViewOnly = control.controls["positionSurCarteForViewOnly"].value;

      if ((typeAvis === null || typeAvis?.length === 0) || (commentaire === null || commentaire?.length === 0)
        || (positionSurCarteForViewOnly === null || positionSurCarteForViewOnly?.length === 0) || (objet === null || objet?.length === 0)) {
        return { notValid: true };
      }

      return null;
    }

    return { notValid: true };
  }
}
